import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router";

import { Button } from "@/components/ui/Button";
import { Dialog } from "@/components/ui/Dialog";
import { apiFetch } from "@/lib/api";
import type { SignOutResult } from "@/lib/api-types";
import { CURRENT_ACTOR_QUERY_KEY, useCurrentActor } from "@/lib/current-actor";
import { heartbeatIntervalMsFor, useIdleTimer } from "@/lib/idle-timer";
import { SESSION_POLICY_QUERY_KEY, useSessionPolicy } from "@/lib/session-policy";

//: Judgement call 5 — the warning's own lead time. Half the real idle
//: window, capped at two real minutes: two minutes is a real, comfortable
//: amount of time to notice a warning and respond to it at the default
//: 15-minute setting (13 minutes idle, 2 to respond), while the cap-as-a-
//: fraction shape means a short, test-only `idle_timeout_minutes` override
//: (VC-15.7-16's own live-check requirement) still gets a real, proportional
//: warning window instead of one that would exceed the whole timeout.
const MAX_WARNING_LEAD_MS = 2 * 60_000;

function warningLeadMsFor(idleTimeoutMs: number): number {
  return Math.min(MAX_WARNING_LEAD_MS, Math.floor(idleTimeoutMs / 2));
}

/**
 * Story 15.7 — mounted once in `RequireAuth.tsx` (judgement call 3), inside
 * the authenticated branch, so it covers every screen behind the auth gate
 * uniformly without any route opting in. Renders nothing but the warning
 * dialog; the actual tracking lives in `useIdleTimer`.
 *
 * The real logout on timeout goes through `POST /auth/sign-out` — the
 * identical server-side revocation `useSignOut` (`NavRail`/`MoreSheet`'s own
 * manual sign-out) already calls — never a client-side-only redirect that
 * would leave a technically-still-valid session behind (VC-15.7-09). It then
 * navigates with `useNavigate()` (react-router's own client-side navigation,
 * not `window.location`), landing on `/sign-in?reason=idle` — deliberately
 * the same in-app-navigation mechanism Story 15.5's per-field unmount
 * cleanup (`InlineEditCell.tsx`) already listens for, so an uncommitted
 * inline edit open on whatever screen the Owner was idle on is auto-saved
 * by that existing mechanism for free, the same way any other in-app
 * navigation away from it already is (judgement call 6 — see this file's
 * own module-level note below for the full reasoning).
 *
 * **Judgement call 6, made explicitly.** The warning dialog's own
 * *appearance* does not itself trigger a save — no new commit-on-warning
 * mechanism was built. What already covers the real gap: Story 15.5's
 * `commitFieldOnLeave` fires from `InlineEditCell`'s own unmount cleanup on
 * *any* in-app navigation, not only a pathname-driven one, and the real
 * timeout below performs exactly that kind of navigation (`navigate()`,
 * never a full-page reload) — so an uncommitted draft open when the timeout
 * actually fires is still auto-saved, the identical existing mechanism,
 * with no new code. What remains a known, accepted, narrow gap: if the
 * Owner ignores the warning and does nothing at all, the draft is only
 * saved at the *final* cutoff, not the moment the warning first appears —
 * acceptable because the warning itself already gives the Owner a real,
 * multi-minute chance to finish or blur the field before that happens,
 * which a silent timeout would not.
 *
 * **Tier A review fix round — the blocking finding.** Genuine client-side
 * activity used to keep only the *local* warning schedule alive; it never
 * itself reached the server. An Owner working continuously on one screen
 * (real activity, but no navigation and no other fetch/mutation) for
 * longer than the idle window would see no warning locally — correctly,
 * since real activity was happening — while the server's own
 * `authenticate_session`/`touch_or_expire_session` (`app/domain/identity/
 * authentication.py`) silently aged their session out from under them, so
 * their next real request (submitting whatever they were working on)
 * landed as a bare, unexplained 401 through `RequireAuth.tsx`'s generic
 * path — no warning ever shown, the honest inactivity message never
 * reached, and likely unsaved work lost on top of it. `useIdleTimer`'s
 * `onHeartbeat` closes this: while genuine local activity continues, this
 * component refetches the already-authenticated `GET /meta/session-policy`
 * periodically (`heartbeatIntervalMsFor`, throttled the same proportional
 * way the warning lead time is) — a real request through the real
 * `authenticate_session` path, so the server's own `last_seen_at` genuinely
 * advances for as long as real activity keeps providing a reason to send
 * one. The heartbeat is driven *by* tracked activity, never counted *as*
 * activity itself — `idle-timer.ts`'s own module doc explains the
 * mechanism in full; refetching the policy query specifically (rather than
 * a bespoke endpoint) reuses data this component already needs, so the
 * heartbeat is never wasted effort even when its authentication side
 * effect is the only reason it was sent.
 */
export interface IdleSessionGuardProps {
  /** Test-only seam: every production call site (`RequireAuth.tsx`) uses
   * the real, real-time defaults — a component test injects a controlled
   * clock/throttle/event target the identical way `idle-timer.test.ts`
   * already does for `startIdleTimer` directly, rather than waiting out
   * real minutes. */
  now?: (() => number) | undefined;
  activityThrottleMs?: number | undefined;
  heartbeatIntervalMs?: number | undefined;
  target?: EventTarget | undefined;
}

export function IdleSessionGuard({
  now,
  activityThrottleMs,
  heartbeatIntervalMs,
  target,
}: IdleSessionGuardProps = {}) {
  const { data: actor } = useCurrentActor();
  const enabled = actor !== undefined;
  const { data: policy, isError: policyFailed } = useSessionPolicy({ enabled });
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const idleTimeoutMs = (policy?.idle_timeout_minutes ?? 15) * 60_000;

  const { warningVisible, stayActive } = useIdleTimer({
    // Tracking starts once the real policy is known EITHER WAY — a
    // resolved value, or a definitive failure that falls through to the
    // sensible `?? 15` default above (Tier A review fix round: the
    // original gate disabled the idle timer entirely, forever, for a tab
    // whose one `GET /meta/session-policy` call happened to fail, silently
    // dropping this story's whole client-side guarantee for that tab even
    // though the server-side enforcement — the actual security boundary —
    // was never affected). Only a still-*pending* first load withholds
    // tracking, the same "safer failure mode while unknown" default this
    // codebase's own `useIsReadOnly` already establishes — starting
    // against a guessed default and re-arming the instant the real value
    // arrives would risk a spurious early warning on a slow first load.
    enabled: enabled && (policy !== undefined || policyFailed),
    idleTimeoutMs,
    warningLeadMs: warningLeadMsFor(idleTimeoutMs),
    onTimeout: () => void handleTimeout(),
    accountId: actor?.id,
    onHeartbeat: () => {
      void queryClient
        .refetchQueries({ queryKey: SESSION_POLICY_QUERY_KEY, exact: true })
        .catch(() => {
          // A failed heartbeat is not itself an error to surface — if the
          // session is genuinely gone, the next real user-triggered
          // request (or the next successful heartbeat) discovers that
          // honestly; a transient failure here must never crash a passive
          // background refresh.
        });
    },
    now,
    activityThrottleMs,
    heartbeatIntervalMs: heartbeatIntervalMs ?? heartbeatIntervalMsFor(idleTimeoutMs),
    target,
  });

  async function handleTimeout() {
    try {
      await apiFetch<SignOutResult>("/auth/sign-out", { method: "POST" });
    } catch {
      // Another tab (or a concurrent request) may already have signed this
      // session out server-side — `sign-out` itself failing (e.g. a 401 on
      // an already-dead session) is not a reason to abandon landing on the
      // sign-in screen with the real, honest reason; the `finally` below
      // still runs regardless. Without this `catch`, the rejection from a
      // failed `apiFetch` here propagated past this `async` function
      // uncaught — `onTimeout: () => void handleTimeout()` discards the
      // returned promise without attaching a handler, so it surfaced as a
      // genuine unhandled promise rejection (Tier A review fix round,
      // confirmed reproducible).
    } finally {
      // Navigate first, and defer clearing the cached actor to a fresh
      // macrotask — found live (real browser, real idle wait,
      // VC-15.7-16): navigating first is not, on its own, enough.
      // `navigate()` only *schedules* the route change; a same-tick cache
      // write right after it can still reach `RequireAuth` while its tree
      // — and its own `useCurrentActor()` observer — has not actually
      // unmounted yet, since React has not necessarily flushed the
      // navigation's own re-render before this synchronous code resumes.
      // When that happens, `RequireAuth`'s *own* generic 401 redirect
      // (`?redirect=<path>`, `RequireAuth.tsx`) reacts to the freshly-401ing
      // actor query and races this component's explicit `?reason=idle`
      // navigate — losing that race silently swaps out the specific
      // inactivity message for the generic one, exactly the failure
      // VC-15.7-09 exists to prevent. A `setTimeout(0)` — a new macrotask,
      // strictly after the browser's own next paint/event-loop turn —
      // reliably runs after React has processed the navigation and torn
      // `RequireAuth`'s tree down.
      //
      // Owner-caught, 2026-08-28: `invalidateQueries()` here (this fix's
      // own prior shape) left a real, reproducible gap — it only marks the
      // query stale and triggers a background refetch; it does not clear
      // the *previous* successful `data` from the cache until that refetch
      // resolves. `SignIn.tsx` itself calls `useCurrentActor()` (to bounce
      // away if already signed in) the moment it mounts, which is exactly
      // what `navigate()` above causes — and it mounted fast enough to read
      // that still-cached, now-stale "signed in" `data` before the deferred
      // refetch had resolved, sending it straight back through `<Navigate
      // to={destination}>` to a screen that immediately 401s on everything,
      // reading as a blank page rather than the sign-in form. `removeQueries`
      // evicts the cached value immediately and synchronously instead of
      // scheduling a refetch against it — the freshly-mounted `SignIn`
      // observes a genuinely empty query (`data: undefined`, `isPending`),
      // never a stale success, so its own already-correct `if
      // (currentActor.data)` check no longer has anything false-positive to
      // fire on. `RequireAuth`'s tree, if a same-tick reaction were somehow
      // still possible, sees the identical "no data" shape rather than a
      // resolved 401 error either, so it does not race this navigate with
      // its own generic redirect.
      navigate("/sign-in?reason=idle", { replace: true });
      setTimeout(() => {
        queryClient.removeQueries({ queryKey: CURRENT_ACTOR_QUERY_KEY, exact: true });
      }, 0);
    }
  }

  if (!enabled) {
    return null;
  }

  return (
    <Dialog
      open={warningVisible}
      onOpenChange={(open) => {
        // Any real interaction with the dialog — including the scrim/Esc
        // dismissal path `dismissible` (the default) already allows — counts
        // as activity (AC: "dismissing/interacting with the warning itself
        // counts as activity and resets the window").
        if (!open) {
          stayActive();
        }
      }}
      title="You'll be signed out soon"
      description="You've been inactive for a while. For your security, PropDesk signs out an idle session automatically."
    >
      <div className="flex justify-end">
        <Button type="button" variant="primary" onClick={stayActive}>
          Stay signed in
        </Button>
      </div>
    </Dialog>
  );
}
